import React, { useEffect, useState } from 'react';
import { ArrowUpRight, Copy, Check, Loader2, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fetchAuth = async (url) => {
  const token = localStorage.getItem('backoffice_token');
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
};

/* Shared Apollo enrichment + AI outreach-copy modal — used by HiringSurge, HiringPressure */
export const CompanyEnrichModal = ({ company, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    setLoading(true);
    setData(null);
    fetchAuth(`${API_URL}/api/intelligence/company/${encodeURIComponent(company)}/enrich`)
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [company]);

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="company-enrich-modal">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <h2 className="text-lg font-bold text-[#0A1F44]">{company}</h2>
            <p className="text-xs text-gray-500">AI-Generated Outreach Materials</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg" data-testid="close-company-enrich-modal"><X className="h-5 w-5 text-gray-400" /></button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 gap-2 text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /> Generating outreach...</div>
        ) : (
          <div className="p-6 space-y-5">
            {data?.decision_makers?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-[#0A1F44] mb-2">Decision Makers</h3>
                <div className="grid grid-cols-2 gap-2">
                  {data.decision_makers.map((dm, i) => (
                    <a key={i} href={dm.linkedin_url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors text-xs" data-testid={`dm-link-${i}`}>
                      <ArrowUpRight className="h-3 w-3 text-blue-600 flex-shrink-0" />
                      <span className="text-blue-700 font-medium truncate">{dm.title}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {data?.outreach_email && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-[#0A1F44]">Email Template</h3>
                  <button onClick={() => copyText(data.outreach_email, 'email')} data-testid="copy-email-btn"
                    className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs text-gray-600 transition-colors">
                    {copied === 'email' ? <><Check className="h-3 w-3 text-green-600" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
                  </button>
                </div>
                <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{data.outreach_email}</pre>
              </div>
            )}

            {data?.linkedin_message && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-[#0A1F44]">LinkedIn Message</h3>
                  <button onClick={() => copyText(data.linkedin_message, 'linkedin')} data-testid="copy-linkedin-btn"
                    className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs text-gray-600 transition-colors">
                    {copied === 'linkedin' ? <><Check className="h-3 w-3 text-green-600" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
                  </button>
                </div>
                <pre className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs text-blue-800 whitespace-pre-wrap font-sans leading-relaxed">{data.linkedin_message}</pre>
              </div>
            )}

            {data?.enrichment && (
              <div>
                <h3 className="text-sm font-semibold text-[#0A1F44] mb-2">Company Intel</h3>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {data.enrichment.industry && <div className="bg-gray-50 p-2 rounded"><span className="text-gray-500">Industry:</span> <span className="font-medium">{data.enrichment.industry}</span></div>}
                  {data.enrichment.employee_count && <div className="bg-gray-50 p-2 rounded"><span className="text-gray-500">Employees:</span> <span className="font-medium">{data.enrichment.employee_count.toLocaleString()}</span></div>}
                  {data.enrichment.estimated_revenue && <div className="bg-gray-50 p-2 rounded"><span className="text-gray-500">Revenue:</span> <span className="font-medium">{data.enrichment.estimated_revenue}</span></div>}
                  {data.enrichment.technologies?.length > 0 && <div className="bg-gray-50 p-2 rounded col-span-2"><span className="text-gray-500">Tech:</span> <span className="font-medium">{data.enrichment.technologies.slice(0, 5).join(', ')}</span></div>}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyEnrichModal;
